import { Box, Text, Accordion, Center, Stack, Popover } from '@chakra-ui/react';
import { StateEnum, useGroupMembers, type User } from '@halo/chat-sdk-react';
import { Empty, UserItem, GroupItemRender, EllipseIcon } from '@halo/ui-chat-react';
import { Portal } from '@halo/ui-react';
import { Fragment } from 'react/jsx-runtime';
import { filter, size, gt, reduce } from 'lodash';
import { Checkmark } from '@halo/ui-react';
import { GroupCreateType } from './helpers';

export type AccordionList = {
  label?: React.ReactNode;
  items: any[];
  key: GroupCreateType;
};

interface AccordionProps {
  list: AccordionList[];
  toggleSelectUser: (user: User) => void;
  selected: User[];
  checkUserKey?: boolean;
}

const Accord = ({ list, toggleSelectUser, selected, checkUserKey = true }: AccordionProps) => {
  const isEmpty = list.every((item) => size(item.items) === 0);
  const [value, setValue] = useState<GroupCreateType[]>([]);
  useEffect(() => {
    const _list = filter(list, (item) => gt(size(item.items), 0));
    const _accord_keys = reduce(
      _list,
      (pre, next) => {
        pre.push(next.key);
        return pre;
      },
      [] as GroupCreateType[]
    );
    setValue(_accord_keys);
  }, [list]);
  return (
    <Box w="full" flex="1">
      {isEmpty && <Empty />}
      <Accordion.Root
        multiple
        collapsible
        variant="plain"
        value={value}
        onValueChange={(e) => {
          setValue(e.value as GroupCreateType[]);
        }}
        defaultValue={[GroupCreateType.MEMBER_CONTACTS]}
      >
        {list.map(({ label, items, key }, index) => {
          if (items?.length === 0) return null;
          return (
            <Accordion.Item key={index} value={key}>
              <Accordion.ItemTrigger
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                {label && (
                  <Text fontSize="xs" fontWeight="bold">
                    {label}
                  </Text>
                )}
                <Accordion.ItemIndicator />
              </Accordion.ItemTrigger>
              <Accordion.ItemContent>
                <Accordion.ItemBody py="0">
                  <Fragment key={index}>
                    <Box>
                      {items?.map((item) => {
                        const isKeyErr = checkUserKey
                          ? !(item.have_group_key && item.have_user_key)
                          : false;
                        const isGroup = item?.isGroup;
                        const isChecked = selected.some((i) => i.user_id === item.user_id);
                        const isDisabled = isKeyErr || item.disabled;

                        const onClick = () => {
                          if (isDisabled) return;
                          toggleSelectUser(item);
                        };

                        const Element = (
                          <Checkmark
                            filled
                            size="sm"
                            borderRadius="sm"
                            mt={isGroup && '-2'}
                            cursor="pointer"
                            disabled={isDisabled}
                            onClick={onClick}
                            checked={isChecked || item.defaultChecked}
                          />
                        );

                        return (
                          <Box key={item?.user_id || item?.gid}>
                            {isGroup ? (
                              <GroupItemRender
                                {...item}
                                startElement={Element}
                                onClick={onClick}
                                cursor="pointer"
                                endElement={<GroupTooltip item={item} />}
                              />
                            ) : (
                              <UserItem
                                {...item}
                                onlyFinalName={true}
                                avatar={item.avatar}
                                error={isKeyErr}
                                startElement={Element}
                                onClick={onClick}
                                cursor="pointer"
                              />
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Fragment>
                </Accordion.ItemBody>
              </Accordion.ItemContent>
            </Accordion.Item>
          );
        })}
      </Accordion.Root>
    </Box>
  );
};

const GroupTooltip = ({ item }: { item: any }) => {
  return (
    <Popover.Root
      positioning={{ strategy: 'fixed', hideWhenDetached: true }}
      lazyMount
      unmountOnExit
    >
      <Popover.Trigger
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Center color="fg.subtle" w="1.5em" h="2em" cursor="pointer">
          <EllipseIcon />
        </Center>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content w="220px">
            <Popover.Arrow>
              <Popover.ArrowTip />
            </Popover.Arrow>
            <Members {...item} />
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
};
interface MembersProps {
  gid: string;
  member_num: number;
}
const Members = (props: MembersProps) => {
  const { t } = useTranslation();
  const { gid, member_num } = props;
  const { members } = useGroupMembers(gid, undefined, { immediate: true });

  return (
    <Box
      w="full"
      py="2"
      px="2"
      maxH="200px"
      overflow="auto"
      css={{
        '&::-webkit-scrollbar-thumb': {
          'background-color': '#ccc !important',
        },
      }}
    >
      <Text color="fg.muted" fontWeight="medium" px="2">
        {member_num} {t('chat.groupMembersText')}
      </Text>
      <Stack alignItems="center" gap="2">
        {members.map((item) => {
          const isKeyErr = !(item.have_group_key && item.have_user_key);
          return (
            <UserItem
              key={item.user_id}
              {...item}
              onlyFinalName={true}
              error={isKeyErr}
              hoverable
              userExtraElement={
                <>
                  {item.state === StateEnum.DELETE_ACCOUNT ? null : (
                    <>
                      {item.role === 1 && (
                        <Center fontSize="xs" fontWeight="bold">
                          <Text color="brand.100">{t('chat.groupRoleOwner')}</Text>
                        </Center>
                      )}
                      {item.role === 2 && (
                        <Center fontSize="xs" fontWeight="bold">
                          <Text color="fg.muted">{t('chat.groupRoleAdmin')}</Text>
                        </Center>
                      )}
                    </>
                  )}
                </>
              }
            />
          );
        })}
      </Stack>
    </Box>
  );
};
const MemoizedAccord = memo(Accord);

export { MemoizedAccord as Accord };
